/**
 * The app's menu (the Navigation review, slice (a), approved by Arjun on 2026-10-06): three areas by how often people
 * use them. Plant is what people read and do every day, Data is the model, devices and imports a plant sets up, and
 * Settings is what an admin touches rarely. Slice (a) changes no page: it sorts today's pages into the areas, in the
 * order of the hubs slice (c) will join them into, and adds Limits and Actions, which had no item.
 *
 * Slice (c) joins pages that belong together into hubs (hubs.ts): one item each, with a strip of tabs over its pages,
 * each tab still at its own address. A hub's item shows when any of its tabs shows.
 *
 * Who sees an item is decided here, from a plain context (MenuContext, rules.ts), so it is tested apart from the
 * layout. An item for something the org does not have (a pack, a switch, a right) stays hidden, never shown as off:
 * Knowledge is hidden where it is not open, where it used to show a "Coming soon" pill. A vendor's org (one that
 * serves other plants and has none of its own) gets Fleet for its admins, Settings, and the Console for platform
 * admins.
 *
 * The consultant has no item (Arjun, 2026-10-06): it opens in the right-hand panel from the top bar's button, the tab
 * at the screen's right edge and Ctrl+J, on the page the person is on. Its own page, for history and long threads, is
 * reached from the panel. An org switch still keeps that page where the new org opens the consultant (OFF_MENU).
 */
import type { LucideIcon } from "lucide-react";
import {
  Bell,
  Building2,
  ClipboardPen,
  Cpu,
  DatabaseZap,
  FileText,
  Gauge,
  GitBranch,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  ListChecks,
  ListTree,
  Settings,
  Shield,
  Sigma,
  Sparkles,
} from "lucide-react";
import { CONSULTANT_PATH } from "@/lib/copilot/availability";
import { HUBS, HUB_PATHS, hubTabs, tabMatches, type HubId } from "./hubs";
import { gateOpen, type Gate, type MenuContext } from "./rules";

export type { MenuContext } from "./rules";

/** One item of the menu: a page at its own address. */
export interface MenuItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** The item is active on its own address alone, never on the pages under it (the Process graph's /app/twin). */
  end?: boolean;
  /** A small dot beside the item: something new since the viewer's last visit (Today's new brief). */
  mark?: string;
  /** A hub's item (slice (c)): the hub, and the tabs its viewer sees, in order. Active on any of their pages. */
  hub?: HubId;
  tabs?: { id: string; to: string; end?: boolean }[];
}

/** An area of the menu, with the items its viewer sees. */
export interface MenuArea {
  id: "plant" | "data" | "settings" | "fleet";
  label: string;
  items: MenuItem[];
}

/** The addresses of the items whose visibility the platform decides per viewer, beyond the pack and the right. */
export const TODAY_PATH = "/app/twin/brief";
export const KNOWLEDGE_PATH = "/app/twin/knowledge";
export const SETTINGS_PATH = "/app/settings";
export const FLEET_PATH = "/app/vendor";
export const DASHBOARDS_PATH = "/app/dashboards";

interface Rule extends Omit<MenuItem, "tabs">, Gate {}

const PLANT: Rule[] = [
  { to: TODAY_PATH, label: "Today", icon: Lightbulb, foundry: true, when: "brief" },
  { to: HUBS.actions.to, label: HUBS.actions.label, icon: ListChecks, hub: "actions" },
  { to: "/app/alarms", label: "Alarms", icon: Bell },
  { to: DASHBOARDS_PATH, label: "Dashboards", icon: LayoutDashboard },
  { to: HUBS.reports.to, label: HUBS.reports.label, icon: FileText, hub: "reports" },
  { to: HUBS.process.to, label: HUBS.process.label, icon: Gauge, hub: "process" },
  { to: HUBS.trace.to, label: HUBS.trace.label, icon: ListTree, hub: "trace" },
  { to: HUBS.capture.to, label: HUBS.capture.label, icon: ClipboardPen, hub: "capture" },
];

const DATA: Rule[] = [
  { to: HUBS["plant-model"].to, label: HUBS["plant-model"].label, icon: GitBranch, end: true, hub: "plant-model" },
  { to: HUBS.devices.to, label: HUBS.devices.label, icon: Cpu, hub: "devices" },
  { to: HUBS["data-sources"].to, label: HUBS["data-sources"].label, icon: DatabaseZap, hub: "data-sources" },
  { to: HUBS.catalog.to, label: HUBS.catalog.label, icon: LayoutGrid, hub: "catalog" },
  { to: "/app/computed-tags", label: "Computed tags", icon: Sigma },
];

const SETTINGS: Rule[] = [
  { to: SETTINGS_PATH, label: "Settings", icon: Settings },
  { to: "/app/platform", label: "Console", icon: Shield, when: "platformAdmin" },
];

const FLEET: Rule[] = [{ to: FLEET_PATH, label: "Fleet", icon: Building2, end: true, when: "vendorAdmin" }];

/**
 * Pages with no menu item that an org switch still keeps, each by its page's own rule: the consultant's, which opens
 * from the top bar and the screen's edge rather than the menu (Arjun, 2026-10-06).
 */
const OFF_MENU: Rule[] = [{ to: CONSULTANT_PATH, label: "Consultant", icon: Sparkles, foundry: true, when: "consultantOpen" }];

/**
 * Every address the menu can hold, whoever views it, a hub's tabs too, and the pages with no item an org switch keeps
 * (OFF_MENU): the pages an org switch may keep (keptPageOf).
 */
export const MENU_PATHS: readonly string[] = [
  ...[...PLANT, ...DATA, ...SETTINGS, ...FLEET, ...OFF_MENU].map((r) => r.to),
  ...HUB_PATHS,
];

/** Whether a page with no menu item (OFF_MENU) is at this address and open to the viewer. */
export function offMenuHolds(ctx: MenuContext, path: string): boolean {
  return !ctx.vendorOrg && itemsOf(OFF_MENU, ctx).some((i) => i.to === path);
}

function itemsOf(rules: Rule[], ctx: MenuContext): MenuItem[] {
  return rules.flatMap(({ to, label, icon, end, hub, ...gate }) => {
    if (!gateOpen(gate, ctx)) return [];
    // A hub's item shows when any of its tabs shows (each tab keeps its page's own rule).
    const tabs = hub ? hubTabs(HUBS[hub], ctx).map((t) => ({ id: t.id, to: t.to, ...(t.end ? { end: t.end } : {}) })) : undefined;
    if (tabs && tabs.length === 0) return [];
    return [
      {
        to,
        label,
        icon,
        ...(end ? { end } : {}),
        ...(hub ? { hub, tabs } : {}),
        ...(to === TODAY_PATH && ctx.briefMark ? { mark: ctx.briefMark } : {}),
      },
    ];
  });
}

/**
 * The menu its viewer sees, in order: Plant, Data and Settings; for a vendor's org, Fleet and Settings. An area with
 * no item is left out.
 */
export function menuOf(ctx: MenuContext): MenuArea[] {
  const areas: MenuArea[] = ctx.vendorOrg
    ? [
        { id: "fleet", label: "Fleet", items: itemsOf(FLEET, ctx) },
        { id: "settings", label: "Settings", items: itemsOf(SETTINGS, ctx) },
      ]
    : [
        { id: "plant", label: "Plant", items: itemsOf(PLANT, ctx) },
        { id: "data", label: "Data", items: itemsOf(DATA, ctx) },
        { id: "settings", label: "Settings", items: itemsOf(SETTINGS, ctx) },
      ];
  return areas.filter((a) => a.items.length > 0);
}

/** Whether the viewer's menu holds an item at this address, or a hub's tab its viewer sees there. */
export function menuHolds(areas: MenuArea[], path: string): boolean {
  return areas.some((a) => a.items.some((i) => i.to === path || !!i.tabs?.some((t) => t.to === path)));
}

/** Whether a hub's item is the open one: its own address, or a page of a tab its viewer sees. */
export function hubItemActive(item: MenuItem, pathname: string): boolean {
  return tabMatches(item, pathname) || !!item.tabs?.some((t) => tabMatches(t, pathname));
}
